import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  emptyLeadForm,
  getLeadFormErrorMessage,
  submitLeadForm,
  type LeadFormInput,
} from "@/lib/leadForms";

const subjectOptions = [
  "Individual Coaching",
  "Corporate Training",
  "Image Consulting",
  "Campus-to-Corporate Training",
  "Handwriting Analysis",
  "ART Therapy for Stress Relief & Confidence",
  "Something Else",
  "General Inquiry",
];

const countryCodes = [
  { code: "+91", label: "🇮🇳 +91" },
  { code: "+971", label: "🇦🇪 +971" },
  { code: "+1", label: "🇺🇸 +1" },
  { code: "+44", label: "🇬🇧 +44" },
  { code: "+61", label: "🇦🇺 +61" },
  { code: "+65", label: "🇸🇬 +65" },
  { code: "+49", label: "🇩🇪 +49" },
  { code: "+33", label: "🇫🇷 +33" },
  { code: "+81", label: "🇯🇵 +81" },
  { code: "+86", label: "🇨🇳 +86" },
  { code: "+966", label: "🇸🇦 +966" },
  { code: "+974", label: "🇶🇦 +974" },
  { code: "+968", label: "🇴🇲 +968" },
  { code: "+973", label: "🇧🇭 +973" },
  { code: "+965", label: "🇰🇼 +965" },
  { code: "+27", label: "🇿🇦 +27" },
  { code: "+234", label: "🇳🇬 +234" },
  { code: "+254", label: "🇰🇪 +254" },
  { code: "+60", label: "🇲🇾 +60" },
  { code: "+63", label: "🇵🇭 +63" },
  { code: "+82", label: "🇰🇷 +82" },
  { code: "+39", label: "🇮🇹 +39" },
  { code: "+34", label: "🇪🇸 +34" },
  { code: "+7", label: "🇷🇺 +7" },
  { code: "+55", label: "🇧🇷 +55" },
  { code: "+52", label: "🇲🇽 +52" },
  { code: "+92", label: "🇵🇰 +92" },
  { code: "+880", label: "🇧🇩 +880" },
  { code: "+94", label: "🇱🇰 +94" },
  { code: "+977", label: "🇳🇵 +977" },
];

interface DiscoveryCallDialogProps {
  children: React.ReactNode;
}

const DiscoveryCallDialog = ({ children }: DiscoveryCallDialogProps) => {
    const { toast } = useToast();
    const [open, setOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [form, setForm] = useState<LeadFormInput>(emptyLeadForm);
    const [countryCode, setCountryCode] = useState("+91");
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setFieldErrors({});
      setIsSubmitting(true);

      try {
        const submissionForm = { ...form, phone: form.phone ? `${countryCode} ${form.phone}` : "" };
        await submitLeadForm(submissionForm, "discovery-call-popup");
        toast({
          title: "Message sent!",
          description: "Mahirah will get back to you within 24 hours.",
        });
        setForm(emptyLeadForm);
        setOpen(false);
      } catch (error) {
        if (error instanceof z.ZodError) {
          const errors: Record<string, string> = {};
          error.issues.forEach((issue) => {
            const field = issue.path[0];
            if (field && !errors[field as string]) errors[field as string] = issue.message;
          });
          setFieldErrors(errors);
        }
        toast({
          title: "Unable to send message",
          description: getLeadFormErrorMessage(error),
          variant: "destructive",
        });
      } finally {
        setIsSubmitting(false);
      }
    };

    return (
      <>
        <span className="contents" onClick={() => setOpen(true)}>
          {children}
        </span>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="overflow-hidden border border-accent/35 bg-transparent p-0 shadow-[0_35px_100px_-24px_hsl(var(--primary)/0.95)] sm:max-w-[560px]">
            <div className="relative rounded-[calc(var(--radius)+0.6rem)] border border-primary-foreground/10 bg-gradient-to-br from-primary via-primary to-secondary p-6 text-primary-foreground sm:p-7">
              <div className="absolute -left-10 top-0 h-36 w-36 rounded-full bg-accent/30 blur-3xl" aria-hidden="true" />
              <div className="absolute -right-8 bottom-0 h-40 w-40 rounded-full bg-primary-foreground/10 blur-3xl" aria-hidden="true" />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,hsl(var(--accent)/0.22),transparent_30%),radial-gradient(circle_at_bottom_left,hsl(var(--background)/0.08),transparent_28%)]" aria-hidden="true" />

              <div className="relative">
                <DialogHeader className="space-y-3 text-left">
                  <div className="inline-flex w-fit items-center rounded-full border border-accent/45 bg-accent/90 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-accent-foreground shadow-[0_12px_30px_-18px_hsl(var(--accent)/0.9)]">
                    Discovery Call
                  </div>
                  <DialogTitle className="max-w-lg text-3xl leading-tight text-primary-foreground">
                    Book a Discovery Call
                  </DialogTitle>
                  <DialogDescription className="max-w-md text-sm leading-6 text-primary-foreground/75">
                    Tell us about your need — Mahirah will respond within 24 hours.
                  </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="discovery-name" className="text-primary-foreground/90">Full Name <span className="text-destructive">*</span></Label>
                    <Input
                      id="discovery-name"
                      name="name"
                      autoComplete="name"
                      value={form.name}
                      onChange={(e) => { setForm({ ...form, name: e.target.value }); setFieldErrors((p) => ({ ...p, name: undefined })); }}
                      placeholder="Your full name"
                      maxLength={100}
                      required
                      disabled={isSubmitting}
                      className="h-11 rounded-2xl border-primary-foreground/15 bg-background/95 text-foreground shadow-[0_14px_35px_-24px_hsl(var(--primary)/0.95)] placeholder:text-muted-foreground/90 focus-visible:ring-2 focus-visible:ring-accent"
                    />
                    {fieldErrors.name && <p className="text-sm text-destructive">{fieldErrors.name}</p>}
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="discovery-email" className="text-primary-foreground/90">Email <span className="text-destructive">*</span></Label>
                      <Input
                        id="discovery-email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        value={form.email}
                        onChange={(e) => { setForm({ ...form, email: e.target.value }); setFieldErrors((p) => ({ ...p, email: undefined })); }}
                        placeholder="you@email.com"
                        maxLength={255}
                        required
                        disabled={isSubmitting}
                        className="h-11 rounded-2xl border-primary-foreground/15 bg-background/95 text-foreground shadow-[0_14px_35px_-24px_hsl(var(--primary)/0.95)] placeholder:text-muted-foreground/90 focus-visible:ring-2 focus-visible:ring-accent"
                      />
                      {fieldErrors.email && <p className="text-sm text-destructive">{fieldErrors.email}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="discovery-phone" className="text-primary-foreground/90">Phone</Label>
                      <div className="flex gap-2">
                        <Select value={countryCode} onValueChange={setCountryCode} disabled={isSubmitting}>
                          <SelectTrigger className="h-11 w-[110px] shrink-0 rounded-2xl border-primary-foreground/15 bg-background/95 text-foreground shadow-[0_14px_35px_-24px_hsl(var(--primary)/0.95)] focus:ring-2 focus:ring-accent">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {countryCodes.map((cc) => (
                              <SelectItem key={cc.code} value={cc.code}>{cc.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          id="discovery-phone"
                          name="phone"
                          type="tel"
                          autoComplete="tel"
                          value={form.phone}
                          onChange={(e) => { const v = e.target.value.replace(/[^0-9\s-]/g, ""); setForm({ ...form, phone: v }); }}
                          placeholder="00000 00000"
                          maxLength={15}
                          disabled={isSubmitting}
                          className="h-11 rounded-2xl border-primary-foreground/15 bg-background/95 text-foreground shadow-[0_14px_35px_-24px_hsl(var(--primary)/0.95)] placeholder:text-muted-foreground/90 focus-visible:ring-2 focus-visible:ring-accent"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-primary-foreground/90">I'm Interested In <span className="text-destructive">*</span></Label>
                    <Select value={form.subject} onValueChange={(v) => { setForm({ ...form, subject: v }); setFieldErrors((p) => ({ ...p, subject: undefined })); }} disabled={isSubmitting}>
                      <SelectTrigger className={`h-11 rounded-2xl border-primary-foreground/15 bg-background/95 text-foreground shadow-[0_14px_35px_-24px_hsl(var(--primary)/0.95)] focus:ring-2 focus:ring-accent ${fieldErrors.subject ? "border-destructive" : ""}`}>
                        <SelectValue placeholder="Select a topic" />
                      </SelectTrigger>
                      <SelectContent>
                        {subjectOptions.map((option) => (
                          <SelectItem key={option} value={option}>{option}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {fieldErrors.subject && <p className="text-sm text-destructive">{fieldErrors.subject}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="discovery-message" className="text-primary-foreground/90">Message <span className="text-destructive">*</span></Label>
                    <Textarea
                      id="discovery-message"
                      name="message"
                      value={form.message}
                      onChange={(e) => { setForm({ ...form, message: e.target.value }); setFieldErrors((p) => ({ ...p, message: undefined })); }}
                      placeholder="Tell us about your goals..."
                      maxLength={1000}
                      rows={3}
                      required
                      disabled={isSubmitting}
                      className="min-h-[100px] rounded-[1.35rem] border-primary-foreground/15 bg-background/95 text-foreground shadow-[0_18px_45px_-28px_hsl(var(--primary)/0.95)] placeholder:text-muted-foreground/90 focus-visible:ring-2 focus-visible:ring-accent"
                    />
                    {fieldErrors.message && <p className="text-sm text-destructive">{fieldErrors.message}</p>}
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="h-12 w-full rounded-full border border-accent/50 bg-accent text-accent-foreground text-sm font-semibold shadow-[0_22px_40px_-20px_hsl(var(--accent)/0.95)] transition-all duration-300 hover:scale-[1.01] hover:bg-accent/95 hover:shadow-[0_28px_55px_-18px_hsl(var(--accent)/1)]"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      "Book Now"
                    )}
                  </Button>
                </form>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
};

export default DiscoveryCallDialog;
